import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { Icon } from './Icon.tsx';

const NativeButton = 'button';

interface RichTextEditorProps {
    value: string;
    onChange: (content: string) => void;
    placeholder?: string;
    readOnly?: boolean;
    className?: string;
    minHeight?: string;
    ariaLabel?: string;
}

const ToolbarButton: React.FC<{
    onClick: () => void;
    isActive?: boolean;
    disabled?: boolean;
    icon: string;
    title: string;
}> = ({ onClick, isActive, disabled, icon, title }) => (
    <NativeButton
        type="button"
        onClick={onClick}
        disabled={disabled}
        title={title}
        className={`rounded p-1.5 transition-colors ${isActive
                ? 'bg-gray-200 text-primary dark:bg-gray-700'
                : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
            }
            ${disabled ? 'cursor-not-allowed opacity-50' : ''}
        `}
    >
        <Icon name={icon} size="sm" />
    </NativeButton>
);

export const RichTextEditor: React.FC<RichTextEditorProps> = ({ 
    value, 
    onChange, 
    placeholder = 'Write something...', 
    readOnly = false,
    className = '',
    minHeight = '150px',
    ariaLabel
}) => {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({ underline: false }),
            Placeholder.configure({
                placeholder,
            }),
            Underline,
        ],
        content: value,
        editable: !readOnly,
        onUpdate: ({ editor }) => {
            onChange(editor.getHTML());
        },
        editorProps: {
            attributes: {
                class: "prose prose-sm dark:prose-invert max-w-none focus:outline-none p-4",
                style: `min-height: ${minHeight}`,
                role: 'textbox',
                'aria-multiline': 'true',
                ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
            },
        },
    });

    // Update content if value changes externally (and not just by the editor itself)
    useEffect(() => {
        if (editor && value !== editor.getHTML() && !editor.isFocused) {
            // Only update if the content is different to avoid cursor jumping
            // This comparison is naive, but might be enough for this use case if we're careful
            // A better approach is to check if focused.
            editor.commands.setContent(value);
        }
    }, [value, editor]);

    useEffect(() => {
         if (editor) {
             editor.setEditable(!readOnly);
         }
    }, [readOnly, editor]);


    if (!editor) {
        return null;
    }

    return (
        <div className={`flex flex-col overflow-hidden rounded-lg border border-gray-300 bg-white focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/50 dark:border-border-dark dark:bg-surface-dark ${className}`}>
            {!readOnly && (
                <div className="flex items-center gap-1 overflow-x-auto border-gray-200 border-b bg-gray-50 px-2 py-1.5 dark:border-border-dark dark:bg-background-dark">
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleBold().run()}
                        isActive={editor.isActive('bold')}
                        icon="format_bold"
                        title="Bold"
                    />
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                        isActive={editor.isActive('italic')}
                        icon="format_italic"
                        title="Italic"
                    />
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleUnderline().run()}
                        isActive={editor.isActive('underline')}
                        icon="format_underlined"
                        title="Underline"
                    />
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleStrike().run()}
                        isActive={editor.isActive('strike')}
                        icon="format_strikethrough"
                        title="Strikethrough"
                    />
                    
                    <div className="mx-1 h-5 w-px bg-gray-300 dark:bg-gray-700" />

                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleBulletList().run()}
                        isActive={editor.isActive('bulletList')}
                        icon="format_list_bulleted"
                        title="Bullet List"
                    />
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleOrderedList().run()}
                        isActive={editor.isActive('orderedList')}
                        icon="format_list_numbered"
                        title="Ordered List"
                    />

                    <div className="mx-1 h-5 w-px bg-gray-300 dark:bg-gray-700" />

                    <ToolbarButton
                         onClick={() => editor.chain().focus().toggleBlockquote().run()}
                         isActive={editor.isActive('blockquote')}
                         icon="format_quote"
                         title="Quote"
                    />
                    <ToolbarButton
                         onClick={() => editor.chain().focus().setHorizontalRule().run()}
                         icon="horizontal_rule"
                         title="Horizontal Rule"
                    />

                    <div className="mx-1 h-5 w-px bg-gray-300 dark:bg-gray-700" />

                    <ToolbarButton
                        onClick={() => editor.chain().focus().undo().run()}
                        disabled={!editor.can().undo()}
                        icon="undo"
                        title="Undo"
                    />
                    <ToolbarButton
                        onClick={() => editor.chain().focus().redo().run()}
                        disabled={!editor.can().redo()}
                        icon="redo"
                        title="Redo"
                    />
                </div>
            )}
            
            {/* biome-ignore lint/a11y/noNoninteractiveElementInteractions: TipTap owns the textbox; this editor surface forwards pointer focus into ProseMirror. */}
            {/* biome-ignore lint/a11y/noStaticElementInteractions: TipTap owns the textbox; this editor surface forwards pointer focus into ProseMirror. */}
            {/* biome-ignore lint/a11y/useKeyWithClickEvents: Keyboard users focus the nested ProseMirror textbox directly; pointer focus is an editor convenience. */}
            <div 
                className="flex-1 cursor-text bg-white dark:bg-surface-dark" 
                onClick={() => editor.chain().focus().run()}
            >
                <EditorContent editor={editor} />
            </div>

            <style>{`
                .ProseMirror p.is-editor-empty:first-child::before {
                    content: attr(data-placeholder);
                    float: left;
                    color: #9ca3af;
                    pointer-events: none;
                    height: 0;
                }
                .ProseMirror:focus {
                    outline: none;
                }
            `}</style>
        </div>
    );
};












